import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Upload, FileText, CheckCircle, AlertCircle, Loader2, X, Download } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { bankingApi, BankAccount } from '../lib/api/banking';
import { getCompanyId } from '../lib/config';
import { Progress } from './ui/progress';

interface BankStatementUploadProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  companyId?: string;
}

export function BankStatementUpload({ 
  open, 
  onOpenChange, 
  onSuccess,
  companyId 
}: BankStatementUploadProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedBankAccount, setSelectedBankAccount] = useState<string>('');
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [extractedText, setExtractedText] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [startTime, setStartTime] = useState<number | null>(null);
  const { toast } = useToast();

  // Load bank accounts when dialog opens
  useEffect(() => {
    if (open) {
      loadBankAccounts();
    } else {
      // Reset state when dialog closes
      setSelectedFile(null);
      setSelectedBankAccount('');
      setUploadResult(null);
      setExtractedText(null);
    }
  }, [open, companyId]);

  const loadBankAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const effectiveCompanyId = companyId || getCompanyId();
      const accounts = await bankingApi.getBankAccounts(effectiveCompanyId);
      setBankAccounts(accounts);
      
      // Auto-select first account if available
      if (accounts.length > 0 && !selectedBankAccount) {
        setSelectedBankAccount(accounts[0].id);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load bank accounts",
        variant: "destructive",
      });
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type - support CSV and Excel only
    const validTypes = [
      'text/csv', 
      'application/vnd.ms-excel', 
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    const validExtensions = ['.csv', '.xls', '.xlsx'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      toast({
        title: "Invalid File Type",
        description: "Please select a CSV or Excel file (.csv, .xls, .xlsx)",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "File size must be less than 10MB",
        variant: "destructive",
      });
      return;
    }

    setSelectedFile(file);
    setUploadResult(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast({
        title: "No File Selected",
        description: "Please select a bank statement file",
        variant: "destructive",
      });
      return;
    }

    if (!selectedBankAccount) {
      toast({
        title: "No Bank Account Selected",
        description: "Please select a bank account",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setProgress(0);
    setProgressMessage('Starting upload...');
    const uploadStartTime = Date.now();
    setStartTime(uploadStartTime);
    setTimeRemaining(null);
    
    // Estimate time remaining based on elapsed time
    // CSV and Excel files are processed quickly
    const fileType = getFileType();
    let estimatedSeconds = 10; // Default estimate for CSV/Excel
    
    // Update progress and time remaining periodically
    const progressInterval = setInterval(() => {
      const elapsed = (Date.now() - uploadStartTime) / 1000;
      const estimatedProgress = Math.min(90, (elapsed / estimatedSeconds) * 100);
      setProgress(estimatedProgress);
      
      if (estimatedProgress > 5) {
        const remaining = Math.max(0, estimatedSeconds - elapsed);
        setTimeRemaining(Math.round(remaining));
      }
    }, 500);
    
    try {
      const effectiveCompanyId = companyId || getCompanyId();
      setProgressMessage('Processing bank statement...');
      
      const result = await bankingApi.uploadBankStatement(
        selectedFile,
        selectedBankAccount,
        effectiveCompanyId
      );
      
      clearInterval(progressInterval);
      setProgress(100);
      setProgressMessage('Complete!');
      setTimeRemaining(0);

      setUploadResult(result);
      
      // Set extracted text if available (even if empty string)
      if (result.extractedText !== undefined) {
        setExtractedText(result.extractedText || '');
      } else {
        setExtractedText('');
      }

      // Build toast message with details
      let toastDescription = `Successfully imported ${result.created} transaction${result.created !== 1 ? 's' : ''}.`;
      const autoReconciledCount = result.autoReconciled || result.duplicateTransactions || 0;
      const newTransactionsCount = result.newTransactions || (result.created - autoReconciledCount);
      
      if (autoReconciledCount > 0) {
        toastDescription += `\n\n✅ ${autoReconciledCount} transaction${autoReconciledCount !== 1 ? 's' : ''} auto-reconciled (matched with internal transactions).`;
      }
      if (newTransactionsCount > 0) {
        toastDescription += `\n\n⚠️ ${newTransactionsCount} new transaction${newTransactionsCount !== 1 ? 's' : ''} require${newTransactionsCount === 1 ? 's' : ''} manual review.`;
      }
      if (result.skipped > 0) {
        toastDescription += `\n\nℹ️ ${result.skipped} transaction${result.skipped !== 1 ? 's' : ''} skipped (already reconciled or duplicates).`;
      }
      if (result.errors > 0) {
        toastDescription += `\n\n❌ ${result.errors} transaction${result.errors !== 1 ? 's' : ''} failed.`;
      }
      
      // Add OCR accuracy reminder only if there are new transactions
      if (newTransactionsCount > 0) {
        toastDescription += `\n\n⚠️ Please review new transactions - OCR may contain errors. Check amounts, dates, and descriptions.`;
      }
      
      toast({
        title: "Upload Successful",
        description: toastDescription,
        duration: 10000, // Show for 10 seconds to read the full message
      });

      // Don't auto-close the modal - let user review the results
      // They can close it manually or it will close when they click "Close" button
      // Call onSuccess callback to refresh data
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      clearInterval(progressInterval);
      setProgress(0);
      setTimeRemaining(null);
      
      // Extract extracted text from error response if available (even if empty string)
      if (error.response?.data?.extractedText !== undefined) {
        setExtractedText(error.response.data.extractedText || '');
      } else {
        // If no extracted text in error, set empty string to show error state
        setExtractedText('');
      }
      
      // Extract detailed error message
      let errorMessage = error.message || "Failed to upload bank statement";
      let errorTitle = "Upload Failed";
      
      // Check if this is an image quality issue
      const errors = error.response?.data?.errors || [];
      const isImageQualityIssue = errors.some((e: string) => 
        e.includes('Image Quality Issue') || 
        e.includes('Low OCR Confidence') || 
        e.includes('blurry') || 
        e.includes('low resolution')
      );
      
      if (isImageQualityIssue) {
        errorTitle = "⚠️ Image Quality Issue";
      } else if (errors.some((e: string) => e.includes('Summary Section Only'))) {
        errorTitle = "⚠️ Summary Section Only";
      }
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      // Format error details better for UI
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Filter out diagnostic messages and format nicely
        const formattedErrors = error.response.data.errors
          .filter((e: string) => !e.startsWith('Total lines') && !e.startsWith('Lines with') && !e.startsWith('Header/footer') && !e.startsWith('Text length') && !e.startsWith('First 200'))
          .map((e: string) => {
            // Remove markdown formatting for toast (toast doesn't support markdown)
            return e
              .replace(/\*\*/g, '') // Remove bold
              .replace(/⚠️/g, '⚠')
              .replace(/💡/g, '💡');
          });
        
        if (formattedErrors.length > 0) {
          // Show first error as main message, rest as details
          errorMessage = formattedErrors[0];
          if (formattedErrors.length > 1) {
            errorMessage += '\n\n' + formattedErrors.slice(1, 5).join('\n');
          }
        }
      }
      
      toast({
        title: errorTitle,
        description: errorMessage,
        variant: "destructive",
        duration: 15000, // Show for 15 seconds to read the details
      });
    } finally {
      setLoading(false);
      setTimeout(() => {
        setProgress(0);
        setProgressMessage('');
        setTimeRemaining(null);
        setStartTime(null);
      }, 2000);
    }
  };

  const getFileIcon = () => {
    if (!selectedFile) return <FileText className="w-8 h-8 text-gray-400" />;
    
    const extension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
    if (extension === '.csv') {
      return <FileText className="w-8 h-8 text-green-500" />;
    } else {
      return <FileText className="w-8 h-8 text-blue-500" />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getFileType = (): 'csv' | 'excel' | null => {
    if (!selectedFile) return null;
    const extension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
    if (extension === '.csv') return 'csv';
    if (['.xls', '.xlsx'].includes(extension)) return 'excel';
    return null;
  };

  const formatTimeRemaining = (seconds: number | null): string => {
    if (seconds === null || seconds === 0) return '';
    if (seconds < 60) return `~${seconds}s remaining`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `~${minutes}m ${secs}s remaining`;
  };

  const downloadTemplate = (format: 'in-out' | 'amount-type' | 'amount-signed' = 'in-out') => {
    let csvContent = '';
    let filename = '';

    if (format === 'in-out') {
      // Format with separate IN and OUT columns (recommended)
      filename = 'bank-statement-template-in-out.csv';
      csvContent = `Date,Description,IN,OUT,Reference
2025-01-15,Cash deposit,5000000,,
2025-01-16,Transfer out,,2500000,TRF-001
2025-01-17,Payment received,1000000,,
2025-01-18,Account maintenance,,5000,
2025-01-19,Salary deposit,2000000,,SAL-001
2025-01-20,Outward cheque,,1500000,`;
    } else if (format === 'amount-type') {
      // Format with single Amount column and Type column
      filename = 'bank-statement-template-amount-type.csv';
      csvContent = `Date,Description,Amount,Type,Reference
2025-01-15,Cash deposit,5000000,Credit,DP-001
2025-01-16,Transfer out,2500000,Debit,
2025-01-17,Payment received,1000000,Credit,
2025-01-18,Account maintenance,5000,Debit,MAINT-001
2025-01-19,Salary deposit,2000000,Credit,SAL-001
2025-01-20,Outward cheque,1500000,Debit,`;
    } else {
      // Format with signed Amount (negative = debit, positive = credit)
      filename = 'bank-statement-template-amount-signed.csv';
      csvContent = `Date,Description,Amount,Reference
2025-01-15,Cash deposit,5000000,DP-001
2025-01-16,Transfer out,-2500000,
2025-01-17,Payment received,1000000,
2025-01-18,Account maintenance,-5000,MAINT-001
2025-01-19,Salary deposit,2000000,SAL-001
2025-01-20,Outward cheque,-1500000,`;
    }

    // Create and download blob
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "Template Downloaded",
      description: `Downloaded ${filename}. Fill in your bank statement data using this format.`,
      duration: 3000,
    });
  };

  const getProgressSteps = () => {
    const fileType = getFileType();
    
    switch (fileType) {
      case 'csv':
        return {
          title: 'Processing CSV file...',
          steps: [
            'Step 1: Reading CSV file...',
            'Step 2: Parsing CSV data...',
            'Step 3: Extracting transactions...',
            'Step 4: Validating transaction data...',
            'Step 5: Creating transactions...'
          ],
          note: 'CSV files are processed quickly'
        };
      case 'excel':
        return {
          title: 'Processing Excel file...',
          steps: [
            'Step 1: Reading Excel file...',
            'Step 2: Parsing spreadsheet data...',
            'Step 3: Extracting transactions...',
            'Step 4: Validating transaction data...',
            'Step 5: Creating transactions...'
          ],
          note: 'Excel files are processed quickly'
        };
      default:
        return {
          title: 'Processing file...',
          steps: [
            'Step 1: Reading file...',
            'Step 2: Parsing data...',
            'Step 3: Extracting transactions...',
            'Step 4: Validating transaction data...',
            'Step 5: Creating transactions...'
          ],
          note: 'CSV and Excel files are processed quickly'
        };
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Upload Bank Statement
          </DialogTitle>
          <DialogDescription>
            Upload a bank statement file (CSV or Excel) to automatically import transactions
          </DialogDescription>
        </DialogHeader>

        {/* OCR Accuracy Warning - Compact */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs font-semibold text-amber-800 mb-1">
                ⚠️ OCR is not 100% accurate - Please review transactions after import
              </p>
              <p className="text-xs text-amber-700">
                Check amounts, dates, and descriptions. Edit/delete incorrect transactions.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6 py-4">
          {/* Bank Account Selection */}
          <div className="space-y-2">
            <Label htmlFor="bankAccount">Bank Account *</Label>
            <Select
              value={selectedBankAccount}
              onValueChange={setSelectedBankAccount}
              disabled={loadingAccounts || loading}
            >
              <SelectTrigger id="bankAccount">
                <SelectValue placeholder="Select a bank account" />
              </SelectTrigger>
              <SelectContent>
                {loadingAccounts ? (
                  <SelectItem value="loading" disabled>Loading accounts...</SelectItem>
                ) : bankAccounts.length === 0 ? (
                  <SelectItem value="no-accounts" disabled>No bank accounts found</SelectItem>
                ) : (
                  bankAccounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.bankName} - {account.accountNumber} ({account.accountType || 'checking'})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {bankAccounts.length === 0 && !loadingAccounts && (
              <p className="text-sm text-muted-foreground">
                Please create a bank account first before uploading statements
              </p>
            )}
          </div>

          {/* Download Templates */}
          <div className="space-y-2">
            <Label>CSV Templates</Label>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => downloadTemplate('in-out')}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Template: IN/OUT Columns
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => downloadTemplate('amount-type')}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Template: Amount + Type
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => downloadTemplate('amount-signed')}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Template: Signed Amount
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Download a CSV template to see the required format. Use IN/OUT columns format for best results. Reference column is optional.
            </p>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label htmlFor="file">Bank Statement File *</Label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <input
                type="file"
                id="file"
                accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleFileSelect}
                className="hidden"
                disabled={loading}
              />
              <label htmlFor="file" className="cursor-pointer">
                {selectedFile ? (
                  <div className="space-y-2">
                    {getFileIcon()}
                    <div>
                      <p className="font-medium text-sm">{selectedFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(selectedFile.size)}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                        setUploadResult(null);
                      }}
                      className="mt-2"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto" />
                    <div>
                      <p className="text-sm font-medium">Click to upload or drag and drop</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        CSV or Excel files (.csv, .xls, .xlsx) - max 10MB
                      </p>
                    </div>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Extracted Text Preview (shown during loading or after processing) */}
          {(loading || extractedText !== null) && (
            <div className="space-y-2">
              <Label>Extraction Status</Label>
              <div className="border rounded-md p-3 bg-green-50/50 border-green-200 max-h-48 overflow-y-auto">
                {loading ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm text-green-700 font-medium">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Processing bank statement...</span>
                        </div>
                        <span className="text-green-600 font-semibold">{Math.round(progress)}%</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                      {progressMessage && (
                        <div className="text-xs text-green-600 font-medium">
                          {progressMessage}
                        </div>
                      )}
                      {timeRemaining !== null && timeRemaining > 0 && (
                        <div className="text-xs text-gray-500">
                          {formatTimeRemaining(timeRemaining)}
                        </div>
                      )}
                    </div>
                  ) : extractedText ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      {extractedText.length > 0 ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="text-green-700 font-medium">
                            Text extracted successfully
                          </span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-4 h-4 text-red-600" />
                          <span className="text-red-700 font-medium">
                            No text could be extracted
                          </span>
                        </>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mb-2">
                      Extracted {extractedText.length} characters from {getFileType() === 'csv' ? 'CSV' : 'Excel'}
                    </div>
                    {extractedText.length > 0 ? (
                      <>
                        <pre className="text-xs font-mono whitespace-pre-wrap break-words text-gray-800 bg-white p-2 rounded border max-h-32 overflow-y-auto">
                          {extractedText.substring(0, 1000)}
                          {extractedText.length > 1000 && '...'}
                        </pre>
                        {extractedText.length > 1000 && (
                          <div className="text-xs text-gray-500">
                            Showing first 1000 of {extractedText.length} characters
                          </div>
                        )}
                        <div className="text-xs text-gray-600 flex gap-3 mt-1">
                          <span>Dates: {extractedText.match(/\d{4}[\/\-]\d{1,2}[\/\-]\d{1,2}|\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4}/g)?.length || 0}</span>
                          <span>Amounts: {extractedText.match(/[\d,]+\.?\d{0,2}/g)?.length || 0}</span>
                          <span>Lines: {extractedText.split('\n').length}</span>
                        </div>
                      </>
                    ) : (
                      <div className="text-xs text-red-600">
                        <p className="font-medium mb-1">
                          ⚠️ No text extracted from {getFileType() === 'csv' ? 'CSV' : 'Excel'}
                        </p>
                        <p>File format may be incorrect or corrupted. Please check the file and try again.</p>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
              {extractedText && extractedText.length > 0 && (
                <p className="text-xs text-gray-500">
                  Extracted text preview. If no transactions found, verify dates and amounts are visible.
                </p>
              )}
            </div>
          )}

          {/* Upload Result */}
          {uploadResult && (
            <div className="space-y-4">
              {/* Post-Upload Warning - Compact */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-amber-800 mb-1">
                      ⚠️ Review Required: Check all transactions for accuracy
                    </p>
                    <p className="text-xs text-amber-700">
                      Verify amounts, dates, and descriptions. Go to Transactions tab to review, edit, or delete incorrect entries. Reconcile manually to ensure accuracy.
                    </p>
                  </div>
                </div>
              </div>

              {/* Success Message - Compact */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-green-700 mb-2">
                  <CheckCircle className="w-4 h-4" />
                  <p className="text-sm font-medium">Upload Successful!</p>
                </div>
                <div className="text-xs text-green-600 space-y-0.5">
                  <p>• Created: {uploadResult.created} transaction{uploadResult.created !== 1 ? 's' : ''}</p>
                  {(uploadResult.autoReconciled || uploadResult.duplicateTransactions || 0) > 0 && (
                    <p className="text-blue-600">• Auto-Reconciled: {uploadResult.autoReconciled || uploadResult.duplicateTransactions || 0} transaction{(uploadResult.autoReconciled || uploadResult.duplicateTransactions || 0) !== 1 ? 's' : ''}</p>
                  )}
                  {uploadResult.newTransactions > 0 && (
                    <p>• New (Requires Review): {uploadResult.newTransactions} transaction{uploadResult.newTransactions !== 1 ? 's' : ''}</p>
                  )}
                  {uploadResult.skipped > 0 && (uploadResult.autoReconciled || uploadResult.duplicateTransactions || 0) === 0 && (
                    <p className="text-amber-600">• Skipped: {uploadResult.skipped} transaction{uploadResult.skipped !== 1 ? 's' : ''}</p>
                  )}
                  {uploadResult.errors > 0 && (
                    <p className="text-red-600">• Errors: {uploadResult.errors} transaction{uploadResult.errors !== 1 ? 's' : ''}</p>
                  )}
                </div>
                {uploadResult.skippedTransactions && uploadResult.skippedTransactions.length > 0 && (
                  <div className={`mt-2 text-xs border rounded p-2 ${
                    (uploadResult.autoReconciled || uploadResult.duplicateTransactions || 0) > 0 
                      ? 'text-blue-600 border-blue-200 bg-blue-50' 
                      : 'text-amber-600 border-amber-200 bg-amber-50'
                  }`}>
                    <p className="font-medium mb-1">
                      {(uploadResult.autoReconciled || uploadResult.duplicateTransactions || 0) > 0 
                        ? `Auto-Reconciled: ${uploadResult.autoReconciled || uploadResult.duplicateTransactions || 0}` 
                        : `Skipped: ${uploadResult.skippedTransactions.length}`}
                    </p>
                    <ul className="list-disc list-inside space-y-0.5 max-h-24 overflow-y-auto text-xs">
                      {uploadResult.skippedTransactions.slice(0, 5).map((skipped: any, index: number) => (
                        <li key={index} className="truncate">
                          {new Date(skipped.date).toLocaleDateString()} - {skipped.amount?.toFixed(2)}
                        </li>
                      ))}
                      {uploadResult.skippedTransactions.length > 5 && (
                        <li className="italic opacity-75">
                          ... and {uploadResult.skippedTransactions.length - 5} more
                        </li>
                      )}
                    </ul>
                  </div>
                )}
                {uploadResult.errorDetails && uploadResult.errorDetails.length > 0 && (
                  <div className="mt-2 text-xs text-red-600 border border-red-200 rounded p-2 bg-red-50">
                    <p className="font-medium mb-1">Errors: {uploadResult.errorDetails.length}</p>
                    <ul className="list-disc list-inside space-y-0.5 max-h-20 overflow-y-auto">
                      {uploadResult.errorDetails.slice(0, 3).map((error: any, index: number) => (
                        <li key={index} className="truncate">
                          {error.date ? `${new Date(error.date).toLocaleDateString()} - ` : ''}
                          {error.error || error.reason || 'Unknown error'}
                        </li>
                      ))}
                      {uploadResult.errorDetails.length > 3 && (
                        <li className="text-red-500 italic">
                          ... and {uploadResult.errorDetails.length - 3} more
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              
              {/* Parsed Transactions Table - Compact */}
              {uploadResult.parsedTransactions && uploadResult.parsedTransactions.length > 0 && (
                <div className="mt-3 border border-gray-300 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-300">
                    <p className="font-semibold text-xs text-gray-700">
                      Parsed Transactions ({uploadResult.parsedTransactions.length})
                    </p>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">Validation</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">Date</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">Description</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-700 border-b">Amount</th>
                          <th className="px-3 py-2 text-center font-semibold text-gray-700 border-b">Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadResult.parsedTransactions.map((tx: any, index: number) => {
                          const isCredit = tx.transactionType === 'credit' || tx.amount > 0;
                          const isDebit = tx.transactionType === 'debit' || tx.amount < 0;
                          const isUncategorized = !tx.transactionType || tx.transactionType === 'uncategorized';
                          const isBalance = tx.isBalance || (!isCredit && !isDebit && !isUncategorized);
                          
                          let statusColor = 'text-gray-600';
                          let statusText = tx.status || 'Uncategorized';
                          if (tx.transactionType === 'credit') {
                            statusColor = 'text-green-600 font-semibold';
                            statusText = 'Credit';
                          } else if (tx.transactionType === 'debit') {
                            statusColor = 'text-red-600 font-semibold';
                            statusText = 'Debit';
                          } else if (tx.transactionType === 'transfer') {
                            statusColor = 'text-blue-600';
                            statusText = 'Transfer';
                          } else if (isBalance) {
                            statusColor = 'text-orange-600';
                            statusText = 'Balance';
                          } else {
                            statusColor = 'text-amber-600';
                            statusText = 'Uncategorized';
                          }
                          
                          // Validation indicator
                          let validationIcon = null;
                          let validationBg = '';
                          if (tx.validationStatus === 'valid') {
                            validationIcon = <CheckCircle className="w-4 h-4 text-green-600" />;
                            validationBg = 'bg-green-50';
                          } else if (tx.validationStatus === 'warning') {
                            validationIcon = <AlertCircle className="w-4 h-4 text-amber-600" />;
                            validationBg = 'bg-amber-50';
                          } else {
                            validationIcon = <AlertCircle className="w-4 h-4 text-red-600" />;
                            validationBg = 'bg-red-50';
                          }
                          
                          return (
                            <tr key={index} className={`border-b border-gray-200 hover:bg-gray-50 ${validationBg}`}>
                              <td className="px-3 py-2 text-center" title={tx.validationMessage || 'Validation status'}>
                                {validationIcon}
                              </td>
                              <td className="px-3 py-2 text-gray-700">
                                {tx.hasDate ? new Date(tx.date).toLocaleDateString() : <span className="text-red-600">Invalid</span>}
                              </td>
                              <td className="px-3 py-2 text-gray-700 max-w-xs truncate" title={tx.description}>
                                {tx.hasDescription ? (tx.description || 'N/A') : <span className="text-amber-600 italic">No description</span>}
                              </td>
                              <td className={`px-3 py-2 text-right font-medium ${
                                isCredit ? 'text-green-600' : isDebit ? 'text-red-600' : 'text-gray-600'
                              }`}>
                                {tx.hasValidAmount ? (
                                  <>
                                    {tx.amount > 0 ? '+' : ''}{tx.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {tx.currency || 'USD'}
                                  </>
                                ) : (
                                  <span className="text-red-600">Invalid</span>
                                )}
                              </td>
                              <td className={`px-3 py-2 text-center ${statusColor}`}>
                                {tx.hasValidType ? (
                                  <span className="font-semibold">{statusText}</span>
                                ) : (
                                  <span className="text-amber-600 italic">Uncategorized</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {/* Validation Summary */}
                  <div className="mt-2 px-4 py-2 bg-gray-50 border-t border-gray-300 text-xs">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-green-600" />
                        <span className="text-gray-600">
                          Valid: {uploadResult.parsedTransactions.filter((tx: any) => tx.validationStatus === 'valid').length}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <AlertCircle className="w-3 h-3 text-amber-600" />
                        <span className="text-gray-600">
                          Warning: {uploadResult.parsedTransactions.filter((tx: any) => tx.validationStatus === 'warning').length}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <AlertCircle className="w-3 h-3 text-red-600" />
                        <span className="text-gray-600">
                          Error: {uploadResult.parsedTransactions.filter((tx: any) => tx.validationStatus === 'error').length}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Totals Comparison - Compact */}
              {uploadResult.totalsSummary && (
                <div className="mt-3 border border-gray-300 rounded-lg overflow-hidden">
                  <div className="bg-blue-50 px-3 py-1.5 border-b border-gray-300">
                    <p className="font-semibold text-xs text-blue-700">
                      Totals Verification
                    </p>
                  </div>
                  <div className="p-3 space-y-2">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Parsed</p>
                        <div className="space-y-0.5 text-xs">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Credit:</span>
                            <span className="font-semibold text-green-600">
                              {uploadResult.totalsSummary.parsedTotals?.totalCredit?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Debit:</span>
                            <span className="font-semibold text-red-600">
                              {uploadResult.totalsSummary.parsedTotals?.totalDebit?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                            </span>
                          </div>
                        </div>
                      </div>
                      {uploadResult.totalsSummary.statementTotals && (
                        <div>
                          <p className="text-xs font-medium text-gray-600 mb-1">Statement</p>
                          <div className="space-y-0.5 text-xs">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Credit:</span>
                              <span className="font-semibold text-green-600">
                                {uploadResult.totalsSummary.statementTotals.totalCredit?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A'}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Debit:</span>
                              <span className="font-semibold text-red-600">
                                {uploadResult.totalsSummary.statementTotals.totalDebit?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A'}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    {uploadResult.totalsSummary.match && (
                      <div className={`mt-2 p-2 rounded text-xs ${
                        uploadResult.totalsSummary.allMatch 
                          ? 'bg-green-50 border border-green-200' 
                          : 'bg-amber-50 border border-amber-200'
                      }`}>
                        <div className="flex items-center gap-2">
                          {uploadResult.totalsSummary.allMatch ? (
                            <CheckCircle className="w-3 h-3 text-green-600" />
                          ) : (
                            <AlertCircle className="w-3 h-3 text-amber-600" />
                          )}
                          <span className={`font-medium ${
                            uploadResult.totalsSummary.allMatch ? 'text-green-700' : 'text-amber-700'
                          }`}>
                            {uploadResult.totalsSummary.allMatch 
                              ? '✅ Totals Match' 
                              : '⚠️ Totals Do Not Match'}
                          </span>
                        </div>
                        {uploadResult.totalsSummary.match.difference && !uploadResult.totalsSummary.allMatch && (
                          <div className="mt-1 text-xs space-y-0.5">
                            {uploadResult.totalsSummary.match.difference.credit && (
                              <p className="text-amber-600">
                                Credit diff: {uploadResult.totalsSummary.match.difference.credit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </p>
                            )}
                            {uploadResult.totalsSummary.match.difference.debit && (
                              <p className="text-amber-600">
                                Debit diff: {uploadResult.totalsSummary.match.difference.debit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Recorded Transactions Summary - Compact */}
              {uploadResult && uploadResult.transactions && uploadResult.transactions.length > 0 && (
                <div className="mt-3 border border-green-300 rounded-lg overflow-hidden bg-green-50">
                  <div className="px-3 py-1.5 border-b border-green-300">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-green-600" />
                      <p className="font-semibold text-xs text-green-700">
                        {uploadResult.created} transaction{uploadResult.created !== 1 ? 's' : ''} recorded
                      </p>
                    </div>
                  </div>
                  <div className="p-2">
                    <p className="text-xs text-green-700">
                      Valid transactions recorded. Review warnings/errors if any.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {uploadResult ? (
            <Button
              onClick={() => {
                onOpenChange(false);
                setExtractedText(null);
                setUploadResult(null);
                setSelectedFile(null);
              }}
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                  setExtractedText(null);
                  setUploadResult(null);
                  setSelectedFile(null);
                }}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={!selectedFile || !selectedBankAccount || loading || bankAccounts.length === 0}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Statement
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

